// Beşiktaş fatura PDF ayrıştırıcı — pdfjs-dist ile text layer çıkarımı
// + Türkçe fatura şablonları için regex-tabanlı alan tespiti.
//
// pdfjs lazy-load edilir ki ana bundle şişmesin.  Worker, CDN'den yüklenir;
// CDN erişimi yoksa pdfjs fallback olarak sync mode kullanır (yavaş ama çalışır).

import type { ParsedInvoice, InvoiceLine } from "./types";
import {
  parseInvoiceDate,
  sha256Hex,
  toNumber,
} from "./common";
import { parsePdfVatBreakdown } from "./vatValidation";

type TextItem = {
  str: string;
  x: number;
  y: number;
  pageIndex: number;
};

async function loadPdfJs(): Promise<any> {
  const pdfjsLib: any = await import(
    /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf.mjs"
  );
  // Worker URL — eşleşen pdfjs-dist sürümüne göre jsdelivr CDN'den
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    const ver = pdfjsLib.version ?? "4.10.38";
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      `https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/legacy/build/pdf.worker.min.mjs`;
  }
  return pdfjsLib;
}

async function extractTextItems(file: File): Promise<{
  items: TextItem[];
  rawText: string;
}> {
  const pdfjsLib = await loadPdfJs();
  const data = await file.arrayBuffer();
  const task = pdfjsLib.getDocument({ data: new Uint8Array(data) });
  const doc = await task.promise;
  const items: TextItem[] = [];
  const parts: string[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    for (const it of content.items as any[]) {
      const str = String(it.str ?? "");
      if (!str) continue;
      // transform = [a, b, c, d, e, f]; e = x, f = y
      const x = Array.isArray(it.transform) ? Number(it.transform[4] ?? 0) : 0;
      const y = Array.isArray(it.transform) ? Number(it.transform[5] ?? 0) : 0;
      items.push({ str, x, y, pageIndex: p - 1 });
      parts.push(str);
    }
    parts.push("\n");
  }
  return { items, rawText: groupIntoLines(items).join("\n") };
}

/** Items'ı sayfa × y-koordinatına göre satırlara gruplar.  Aynı y ± tolerans
 *  aynı satırdır; x'e göre sıralanır. */
function groupIntoLines(items: TextItem[]): string[] {
  const yTol = 2.5;
  const byPage = new Map<number, TextItem[]>();
  for (const it of items) {
    const arr = byPage.get(it.pageIndex) ?? [];
    arr.push(it);
    byPage.set(it.pageIndex, arr);
  }
  const lines: string[] = [];
  const pages = Array.from(byPage.keys()).sort((a, b) => a - b);
  for (const p of pages) {
    const arr = (byPage.get(p) ?? []).slice().sort((a, b) => b.y - a.y);
    let currentY = Number.NaN;
    let bucket: TextItem[] = [];
    const flush = () => {
      if (bucket.length === 0) return;
      bucket.sort((a, b) => a.x - b.x);
      lines.push(
        bucket
          .map((b) => b.str)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim(),
      );
      bucket = [];
    };
    for (const it of arr) {
      if (Number.isNaN(currentY) || Math.abs(it.y - currentY) <= yTol) {
        bucket.push(it);
        if (Number.isNaN(currentY)) currentY = it.y;
      } else {
        flush();
        currentY = it.y;
        bucket.push(it);
      }
    }
    flush();
  }
  return lines.filter((l) => l.length > 0);
}

const META_LINE_PATTERNS: Array<{
  key:
    | "invoice_no"
    | "invoice_date"
    | "supplier_name"
    | "supplier_tax_no"
    | "subtotal"
    | "vat_total"
    | "grand_total";
  re: RegExp;
}> = [
  { key: "invoice_no", re: /fatura\s*(no|numaras[ıi])\s*[:：]?\s*([A-Z0-9\-\/]+)/i },
  { key: "invoice_date", re: /fatura\s*tarihi\s*[:：]?\s*(\d{1,2}[./-]\d{1,2}[./-]\d{2,4})/i },
  { key: "supplier_tax_no", re: /vergi\s*(no|numaras[ıi]|kimlik(?:\s*no)?)\s*[:：]?\s*(\d{10,11})/i },
  // Beşiktaş e-faturalarında düzenleyen firma unvanı üst bölümde etiketsiz görünür.
  { key: "supplier_name", re: /(BEŞİKTAŞ\s+SPORTİF\s+ÜRÜNLER\s+SAN\.?\s*TİC\.?\s*A\.?Ş\.?)/i },
  // Bu şablon toplam etiketlerini "Tutarı" ekiyle ve bazen çok satırlı KDV açıklamasıyla verir.
  { key: "subtotal", re: /(?:mal\s*hizmet\s*toplam(?:\s*tutar[ıi])?|ara\s*toplam)\s*[:：]?\s*([\d.,]+)\s*TL/i },
  { key: "vat_total", re: /(?:kdv\s*toplam[ıi]|toplam\s*kdv)\s*[:：]?\s*([\d.,]+)\s*TL/i },
  { key: "grand_total", re: /(?:vergiler\s*dahil\s*toplam\s*tutar|genel\s*toplam|ödenecek\s*tutar|odenecek\s*tutar|net\s*tutar)\s*[:：]?\s*([\d.,]+)\s*TL/i },
];


export function normalizePdfLineSpacing(line: string): string {
  // Some PDF text layers join the currency marker and next amount (e.g. "TL20.792,07").
  // Separate them before numeric tokenization so the leading digit is not lost.
  return line.replace(/\s+/g, " ").replace(/TL(?=\d)/gi, "TL ").replace(/Adet(?=\d)/gi, "Adet ").trim();
}

/** Beşiktaş faturalarında beden bilgisi çoğu zaman ürün satırının alt satırındadır.
 * Bu satırı önceki ürün satırına ekleyerek bedenin ayrıştırma sırasında kaybolmasını önler. */
export function mergePdfSizeContinuationLines(lines: string[]): string[] {
  const merged: string[] = [];
  for (const line of lines) {
    const clean = normalizePdfLineSpacing(line);
    // The PDF layout puts the size on a separate baseline; it may be followed
    // by the numeric columns on another baseline. Keep size attached to its item.
    if (/^BEDEN\s*:/i.test(clean) && merged.length > 0) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${clean}`;
      continue;
    }
    // In the original invoice, the description (and item number) is on one
    // baseline while "6 Adet 3.465,345 TL ..." is on the next. Join them before
    // parsing; otherwise the amounts are orphaned and the product is dropped.
    if (
      /^\d{1,6}\s+Adet\s+/i.test(clean) &&
      merged.length > 0 &&
      /^\s*\d{1,4}[.)]?\s+/.test(merged[merged.length - 1]) &&
      !/\b\d{1,6}\s+Adet\s+/i.test(merged[merged.length - 1])
    ) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${clean}`;
      continue;
    }
    merged.push(clean);
  }
  return merged;
}

export function tryParseInvoiceLine(line: string, lineNo: number = 1): InvoiceLine | null {
  const clean = normalizePdfLineSpacing(line);

  // Beşiktaş'ın e-fatura PDF'sinde miktar, birim fiyat ve TL tutarları
  // sütunlar halinde çıkar. Ürün adındaki sezon (26-27) ve kod rakamları
  // sayısal sütunlara karışmasın diye önce "Adet + Birim Fiyat + KDV + Tutar"
  // dizilimini doğrudan yakalıyoruz. Birim fiyat 3-6 ondalık basamak içerebilir.
  const columnMatch = clean.match(
    /^\s*\d{1,4}[.)]?\s+(.+?)\s+(\d{1,6})\s+Adet\s+([\d.]+,\d{1,6})\s*TL\s+.*?%\s*(\d{1,2})\s+([\d.]+,\d{2})\s*TL\s+([\d.]+,\d{2})\s*TL\b/i,
  );
  if (columnMatch) {
    const productText = columnMatch[1].trim();
    const qty = toNumber(columnMatch[2]);
    const unit_price = toNumber(columnMatch[3]);
    const vat_rate = toNumber(columnMatch[4]);
    const vat_amount = toNumber(columnMatch[5]);
    const line_total = toNumber(columnMatch[6]);
    if (qty != null && qty > 0 && unit_price != null && unit_price >= 0 &&
        vat_rate != null && vat_amount != null && line_total != null) {
      const expected = qty * unit_price;
      // Faturadaki birim fiyat hassasiyetini koru; satır toplamı ile kuruş
      // yuvarlamasından kaynaklanan farkı tolere et, büyük farkı sessizce kabul etme.
      if (Math.abs(expected - line_total) <= Math.max(0.05, qty * 0.00001) ||
          Math.abs(expected * (1 + vat_rate / 100) - line_total) <= 0.05) {
        // Renk etiketi PDF metin katmanında bazen virgül, bazen yalnızca boşlukla ayrılır.
        // Renk alanını BEDEN etiketine veya ürün metninin sonuna kadar al; ayırıcı şart koşma.
        const colorMatch = productText.match(/Renk\s*:\s*([^,;]+?)(?=\s*(?:[,;]|BEDEN\s*:|$))/i);
        const color_name = colorMatch?.[1]?.trim() || null;
        let cleanedProduct = productText
          .replace(/[,;]?\s*Renk\s*:\s*[^,;]+/i, "")
          .replace(/\s*[,;]\s*$/, "")
          .replace(/[,;]?\s*BEDEN\s*:\s*[^,;\s]+/i, "")
          .trim();
        let product_code: string | null = null;
        const codeMatch = cleanedProduct.match(/^((?=[A-Z0-9\-_.\/]*\d)[A-Z0-9][A-Z0-9\-_.\/]{2,})\s+(.+)$/);
        if (codeMatch) {
          product_code = codeMatch[1];
          cleanedProduct = codeMatch[2].trim();
        }
        return {
          line_no: lineNo, product_code, product_name: cleanedProduct,
          color_name, size: clean.match(/BEDEN\s*:\s*([^,;\s]+)/i)?.[1]?.trim() || null,
          qty, unit: "Adet", unit_price, vat_rate, vat_amount, line_total,
          match: "unknown", issues: [],
        };
      }
    }
  }

  // Fatura üstbilgileri ve toplam satırları ürün değildir.
  if (
    /\b(mersis|müşteri\s*no|musteri\s*no|telefon|fax|e-?posta|vergi\s*(no|dairesi)|fatura\s*(no|tarihi|tipi)|irsaliye|sipariş|senaryo|genel\s*toplam|ara\s*toplam|ödenecek|mal\s*hizmet\s*toplam|kdv\s*toplam)\b/i.test(clean)
  ) return null;

  // Sayıları konumlarıyla birlikte al; ürün adındaki sayıları da koru.
  const re = /(?<![A-Za-z])[-+]?(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d+|\.\d+)?/g;
  // A size such as 3XL contains a digit; exclude the BEDEN label and value
  // from numeric-tail detection so it cannot shift quantity/price columns.
  const numericText = clean.replace(/\s*BEDEN\s*:\s*[^,;\s]+/gi, "");
  const matches = Array.from(numericText.matchAll(re));
  if (matches.length < 3) return null;

  const parsed = matches.map((m) => ({
    raw: m[0],
    start: m.index ?? 0,
    value: toNumber(m[0]),
  })).filter((m) => m.value != null);

  if (parsed.length < 3) return null;

  // Son sütunlar: miktar, birim fiyat, KDV oranı/tutarı ve satır toplamı.
  const tail = parsed.slice(-5);
  let qty: number | null = null;
  let unit_price: number | null = null;
  let vat_rate: number | null = null;
  let vat_amount: number | null = null;
  let line_total: number | null = null;

  if (tail.length >= 5) {
    [qty, unit_price, vat_rate, vat_amount, line_total] =
      tail.slice(-5).map((n) => n.value as number);
  } else if (tail.length === 4) {
    [qty, unit_price, vat_rate, line_total] =
      tail.map((n) => n.value as number);
  } else {
    [qty, unit_price, line_total] =
      tail.slice(-3).map((n) => n.value as number);
  }

  if (
    qty == null || qty <= 0 ||
    unit_price == null || unit_price < 0 ||
    line_total == null || line_total < 0
  ) return null;

  if (vat_rate != null && (vat_rate < 0 || vat_rate > 30)) {
    vat_rate = null;
  }

  // Miktar × birim fiyat kontrolü. Satır toplamı KDV hariç veya KDV dahil
  // gelebilir; her iki olasılığı da kontrol et. İndirim/ek ücret nedeniyle
  // eşleşmeyen satırlar otomatik kabul edilmez; manuel incelemeye bırakılır.
  const expected = qty * unit_price;
  if (expected > 0) {
    const tolerance = 0.05;
    const matches = (candidate: number) =>
      Math.abs(candidate - line_total!) / Math.max(Math.abs(candidate), 0.01) <= tolerance;
    const matchesBeforeVat = matches(expected);
    const matchesAfterVat = vat_rate != null
      ? matches(expected * (1 + vat_rate / 100))
      : false;
    if (!matchesBeforeVat && !matchesAfterVat) return null;
  }

  // Ürün adını son sayısal sütunların başladığı yerden önce al.
  const firstTail = tail[0];
  if (!firstTail) return null;

  let productText = numericText.slice(0, firstTail.start)
    .replace(/^\d{1,3}[.)]?\s+/, "")
    .trim();

  // Renk ve beden alanlarını ürün açıklamasından ayır.
  const colorMatch = productText.match(/(?:^|[,;]\s*)Renk\s*:\s*([^,;]+?)(?=\s*(?:[,;]|BEDEN\s*:|$))/i);
  const sizeMatch = clean.match(/BEDEN\s*:\s*([^,;\s]+)/i);
  const color_name = colorMatch?.[1]?.trim() || null;
  const size = sizeMatch?.[1]?.trim() || null;
  productText = productText
    .replace(/[,;]?\s*Renk\s*:\s*[^,;]+/i, "")
    .replace(/\s*[,;]\s*$/, "")
    .trim();

  // Baştaki ürün kodunu ayır.
  let product_code: string | null = null;
  const codeMatch = productText.match(/^((?=[A-Z0-9\-_.\/]*\d)[A-Z0-9][A-Z0-9\-_.\/]{2,})\s+(.+)$/);
  if (codeMatch) {
    product_code = codeMatch[1];
    productText = codeMatch[2].trim();
  }

  if (productText.length < 2) return null;

  return {
    line_no: lineNo,
    product_code,
    product_name: productText,
    color_name,
    size,
    qty,
    unit: null,
    unit_price,
    vat_rate,
    vat_amount,
    line_total,
    match: "unknown",
    issues: [],
  };
}



export async function parseBesiktasPdf(file: File): Promise<ParsedInvoice> {
  const buf = await file.arrayBuffer();
  const file_hash = await sha256Hex(buf);
  const result: ParsedInvoice = {
    file_name: file.name,
    file_hash,
    source: "pdf",
    meta: {
      invoice_no: null,
      invoice_date: null,
      supplier_name: null,
      supplier_tax_no: null,
      totals_include_vat: null,
      subtotal: null,
      vat_total: null,
      grand_total: null,
    },
    lines: [],
    issues: [],
    warn_count: 0,
    error_count: 0,
  };

  let items: TextItem[];
  let rawText: string;
  try {
    const extracted = await extractTextItems(file);
    items = extracted.items;
    rawText = extracted.rawText;
  } catch (e) {
    result.issues.push({
      code: "pdf_read_failed",
      message:
        e instanceof Error
          ? `PDF okunamadı: ${e.message}`
          : "PDF okunamadı (bilinmeyen hata).",
      severity: "error",
    });
    result.error_count++;
    return result;
  }

  result.raw_text = rawText;

  if (items.length === 0) {
    result.issues.push({
      code: "pdf_no_text",
      message:
        "PDF'de metin katmanı bulunamadı (muhtemelen taranmış görüntü). OCR gerektirir.",
      severity: "error",
    });
    result.error_count++;
    return result;
  }

  // Üstbilgi — tüm ham metin üzerinde regex
  for (const { key, re } of META_LINE_PATTERNS) {
    const m = rawText.match(re);
    if (!m) continue;
    const val = m[m.length - 1]?.trim();
    if (!val) continue;
    if (key === "invoice_date") {
      result.meta.invoice_date = parseInvoiceDate(val);
    } else if (key === "subtotal" || key === "vat_total" || key === "grand_total") {
      const n = toNumber(val);
      if (n != null) result.meta[key] = n;
    } else {
      (result.meta as any)[key] = val;
    }
  }

  // Beşiktaş'ın PDF şablonunda KDV satırı uzun açıklamayla gelir; tutarı satırın
  // sonundaki TL değerinden al. "KDV %10" oranını tutar sanmamak için bu ayrım önemli.
  const lines = mergePdfSizeContinuationLines(groupIntoLines(items));
  const moneyAtLineEnd = (line: string) => {
    const matches = Array.from(line.matchAll(/([\d.]+,\d{2})\s*TL\b/g));
    const raw = matches.at(-1)?.[1];
    return raw ? toNumber(raw) : null;
  };
  const subtotalLine = lines.find((line) =>
    /mal\s*hizmet\s*toplam\s*tutar[ıi]/i.test(line)
  );
  if (subtotalLine) {
    const amount = moneyAtLineEnd(subtotalLine);
    if (amount != null) result.meta.subtotal = amount;
  }
  // Some Beşiktaş invoices include separate VAT totals by rate (e.g. 10% and 20%).
  // Sum every explicitly labelled rate line instead of stopping at the first VAT amount.
  const vatBreakdown = parsePdfVatBreakdown(lines);
  if (vatBreakdown.length > 0) {
    result.meta.vat_total = Math.round(
      (vatBreakdown.reduce((sum, item) => sum + item.amount, 0) + Number.EPSILON) * 100
    ) / 100;
  } else {
    const vatLineIndex = lines.findIndex((line) =>
      /katma\s*değer|kdv\s*toplam[ıi]|toplam\s*kdv/i.test(line)
    );
    if (vatLineIndex >= 0) {
      // Fallback for layouts with a single VAT total and no explicit rate breakdown.
      for (const line of lines.slice(vatLineIndex, vatLineIndex + 4)) {
        const amount = moneyAtLineEnd(line);
        if (amount != null) {
          result.meta.vat_total = amount;
          break;
        }
      }
    }
  }
  const grandTotalLine = lines.find((line) =>
    /vergiler\s*dahil\s*toplam\s*tutar|ödenecek\s*tutar/i.test(line)
  );
  if (grandTotalLine) {
    const amount = moneyAtLineEnd(grandTotalLine);
    if (amount != null) result.meta.grand_total = amount;
  }

  // Kalem satırlarını yakala — en az 3 sayı içeren ve ürün gibi görünen satırlar
  let lineNo = 0;
  for (const l of lines) {
    
    // Firma ve fatura üstbilgilerini ürün kalemi olarak asla kabul etme.
    if (/\b(mersis|müşteri\s*no|musteri\s*no|fax|telefon|e-?posta|internet\s*sitesi|vergi\s*(no|dairesi)|fatura\s*(no|tarihi|tipi)|irsaliye|sipariş|senaryo|versiyon)\b/i.test(l)) continue;

    // Başlık ve üstbilgi satırlarını atla
    if (/mal\s*hizmet|miktar|birim\s*fiyat|kdv/i.test(l) && !/\d/.test(l))
      continue;
    if (/^(fatura|satici|al[ıi]c[ıi]|vergi|unvan|adres|tel|fax|e[\-\s]?posta)/i.test(l))
      continue;
    if (/^(ara\s*toplam|genel\s*toplam|kdv\s*toplam|mal\s*hizmet\s*toplam|odenecek)/i.test(l))
      continue;
    const line = tryParseInvoiceLine(l, lineNo + 1);
    if (line) {
      lineNo = line.line_no;
      result.lines.push(line);
    }
  }

  if (result.lines.length === 0) {
    result.issues.push({
      code: "pdf_no_lines",
      message:
        "PDF metni okundu ama fatura kalemleri otomatik ayrıştırılamadı. Ham metni önizlemede inceleyin ve manuel düzeltin.",
      severity: "warning",
    });
    result.warn_count++;
  }

  // Satır doğrulamaları (basit)
  for (const line of result.lines) {
    if (line.qty == null) {
      line.issues.push({ code: "qty_missing", message: "Miktar okunamadı.", severity: "error" });
      result.error_count++;
    }
    if (line.unit_price == null && line.line_total == null) {
      line.issues.push({ code: "price_missing", message: "Fiyat/toplam okunamadı.", severity: "error" });
      result.error_count++;
    }
  }

  // Header totals must reconcile independently of whether parsed line totals
  // are VAT-exclusive (subtotal) or VAT-inclusive (grand total).
  const parsedLineTotal = result.lines.length > 0
    ? result.lines.reduce((sum, line) => sum + (line.line_total ?? 0), 0)
    : null;
  if (parsedLineTotal != null && result.meta.subtotal != null && result.meta.grand_total != null) {
    const matchesSubtotal = Math.abs(parsedLineTotal - result.meta.subtotal) <= 0.05;
    const matchesGrandTotal = Math.abs(parsedLineTotal - result.meta.grand_total) <= 0.05;
    if (matchesSubtotal && !matchesGrandTotal) {
      result.meta.totals_include_vat = false;
    } else if (matchesGrandTotal && !matchesSubtotal) {
      result.meta.totals_include_vat = true;
    } else if (!matchesSubtotal && !matchesGrandTotal) {
      result.issues.push({
        code: "pdf_line_total_mismatch",
        message: `Okunan ürün satırları toplamı (${parsedLineTotal.toFixed(2)} TL) ara toplamla (${result.meta.subtotal.toFixed(2)} TL) veya genel toplamla (${result.meta.grand_total.toFixed(2)} TL) uyuşmuyor. Eksik ya da yanlış okunan kalemleri kontrol edin.`,
        severity: "error",
      });
      result.error_count++;
    } else {
      result.issues.push({
        code: "pdf_line_total_ambiguous",
        message: "Satır toplamlarının KDV durumu yalnızca tutarlardan kesin belirlenemedi. Orijinal faturayı kontrol edin.",
        severity: "warning",
      });
      result.warn_count++;
    }
  }

  // Header arithmetic check; this does not determine the VAT status of line totals.
  if (result.meta.subtotal != null && result.meta.vat_total != null && result.meta.grand_total != null) {
    const calculated = result.meta.subtotal + result.meta.vat_total;
    if (Math.abs(calculated - result.meta.grand_total) > 0.05) {
      result.issues.push({
        code: "pdf_header_totals_mismatch",
        message: "Ara toplam + KDV, genel toplamla uyuşmuyor. Orijinal faturayı kontrol edin.",
        severity: "error",
      });
      result.error_count++;
    }
  }

  return result;
}
